import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button } from "@air/primitive-button";
import { Modal, ModalCloseButton, ModalTitle } from "@air/primitive-modal";
import { useToasts } from "@air/provider-toast";
import differenceBy from "lodash/differenceBy";
import Link from "next/link";
import pluralize from "pluralize";
import React, { useState } from "react";
import { PrivateTagsSelect } from "~/components/TagsSelect/PrivateTagsSelect";
import { Routes } from "~/constants/routes";
import { useShowSubscriptionExpiredModal } from "~/hooks/useShowSubscriptionExpiredModal";
import { useAddTagsToClips } from "~/swr-hooks/tags/useAddTagsToClips";
import { useRemoveTagsFromClips } from "~/swr-hooks/tags/useRemoveTagsFromClips";
import { insertIf } from "~/utils/insertIf";
import { useRemoveAssetsWithoutTagsFromFilteredViews, useUpdateAssetsInAllViews } from "~/utils/mutateUtils/GalleryAssets";
import { getBoardIdFromPath } from "~/utils/PathUtils";
export const EditClipsTagsModal = (param)=>{
    let { onClose, clipIds, trackLocation, initialTags } = param;
    const { showToast } = useToasts();
    const { addTagsToClips } = useAddTagsToClips();
    const { removeTagsFromClips } = useRemoveTagsFromClips();
    const { updateAssetsInAllViews } = useUpdateAssetsInAllViews();
    const { removeAssetsWithoutTagsFromFilteredViews } = useRemoveAssetsWithoutTagsFromFilteredViews();
    const [saving, setSaving] = useState(false);
    const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({
        onClose
    });
    const [editedTags, setEditedTags] = useState(initialTags);
    const handleSubmit = async ()=>{
        setSaving(true);
        try {
            const tagsToAdd = differenceBy(editedTags, initialTags, "id");
            const tagsToRemove = differenceBy(initialTags, editedTags, "id");
            await Promise.all([
                ...insertIf(tagsToAdd.length > 0, ()=>[
                        addTagsToClips({
                            tags: tagsToAdd,
                            clipIds,
                            trackLocation
                        })
                    ]),
                ...insertIf(tagsToRemove.length > 0, ()=>[
                        removeTagsFromClips({
                            tags: tagsToRemove,
                            clipIds,
                            trackLocation
                        })
                    ])
            ]);
            const tagsToRemoveIds = tagsToRemove.map((param)=>{
                let { id } = param;
                return id;
            });
            updateAssetsInAllViews({
                assetIdentifiers: clipIds,
                identifier: "id",
                boardId: getBoardIdFromPath(window.location.pathname),
                updateFn: (clip)=>{
                    const filteredClipTags = clip.tags.filter((param)=>{
                        let { id } = param;
                        return !tagsToRemoveIds.includes(id);
                    });
                    clip.tags = [
                        ...filteredClipTags,
                        ...tagsToAdd.filter((tag)=>!clip.tags.find((t)=>t.id === tag.id)).map((tag)=>({
                                ...tag,
                                isDemo: false
                            }))
                    ];
                    return clip;
                }
            });
            removeAssetsWithoutTagsFromFilteredViews({
                clipIds,
                changedTags: [
                    ...tagsToAdd,
                    ...tagsToRemove
                ]
            });
            showToast("Tags saved on ".concat(pluralize("asset", clipIds.length, true)));
            onClose();
        } catch (_error) {
            setSaving(false);
            showToast("An error occurred - please try again later");
        }
    };
    if (showingSubscriptionExpiredModal) {
        return null;
    }
    return /*#__PURE__*/ _jsx(Modal, {
        "data-testid": "EDIT_CLIPS_TAGS_MODAL",
        isOpen: true,
        onDismiss: onClose,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col gap-6",
            children: [
                /*#__PURE__*/ _jsxs("header", {
                    className: "flex justify-between gap-4",
                    children: [
                        /*#__PURE__*/ _jsx(ModalTitle, {
                            children: "Edit tags"
                        }),
                        /*#__PURE__*/ _jsx(ModalCloseButton, {
                            className: "shrink-0",
                            onClick: onClose
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "text-16 text-grey-11",
                    children: [
                        "Add or remove tags from the ",
                        pluralize("item", clipIds.length),
                        " you’ve selected. Manage your workspace’s tags in",
                        " ",
                        /*#__PURE__*/ _jsx(Link, {
                            href: Routes.workspaceAdmin.tags,
                            target: "_blank",
                            children: "settings"
                        }),
                        "."
                    ]
                }),
                /*#__PURE__*/ _jsx(PrivateTagsSelect, {
                    size: "extra-large",
                    selectedTags: editedTags,
                    onSelectedTagsChanged: setEditedTags,
                    canCreateNewTags: true
                }),
                /*#__PURE__*/ _jsxs("footer", {
                    className: "flex justify-end gap-3",
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            "data-testid": "CANCEL_CLIPS_TAGS_CHANGE_BUTTON",
                            appearance: "ghost",
                            color: "grey",
                            onClick: onClose,
                            size: "large",
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            "data-testid": "SAVE_CLIPS_TAGS_BUTTON",
                            onClick: handleSubmit,
                            isLoading: saving,
                            disabled: saving || editedTags.some((param)=>{
                                let { id } = param;
                                return !id;
                            }),
                            appearance: "filled",
                            color: "blue",
                            size: "large",
                            children: "Save"
                        })
                    ]
                })
            ]
        })
    });
};
